// client/src/features/quotations/addCatalogueItemFromLine.ts
//
// `ItemLookupPanel`'s "+ Add this item to Master Catalogue", as an
// orchestration function — DEPENDENCY-INJECTED for the two network calls it
// makes, so this can be unit-tested against mocked API responses without
// rendering `QuotationDetailPage` (a god file under a line ceiling,
// TECH_DEBT #26; `QuotationDetailPage.handleAddToCatalogueFromLookup` is a
// thin wrapper around this that supplies the real API functions and the
// page's `setQuotation` / `setLookupPanelOpenIndex` side effects).
//
// Two things happen on success (the product spec, verbatim): the catalogue
// submission is created, AND the line is priced immediately from what was
// typed — the quote goes out today and must not wait on an admin. ONE
// failure, ONE outcome: a currency this quotation holds no rate for is
// refused before anything is created, and a failed submission leaves the
// line untouched (never priced). Pricing itself calls
// `linePriceApplication.applyLinePriceFacts` — the SAME owner the catalogue
// pick (`QuotationDetailPage.handleSearchSelect`) prices through — so a typed
// price and a picked one are never two copies of the same money math.

import type { QuotationDetailDto, QuotationItemDto } from '../../lib/quotationApi';
import type { CreateCatalogueItemPayload } from '../../lib/masterCatalogueApi';
import type {
  SubmitCatalogueAdditionBody,
  SubmitCatalogueAdditionResponse,
} from '../../lib/catalogueSubmissionsApi';
import { usdCostFacts } from '../../utils/fxDisplay';
import { ceilPrice } from '../../utils/priceUtils';
import { markupToApply } from '../../utils/pricing';
import { catalogueRowCostPrice, catalogueRowIsTtd } from '../catalogue/costPriceTiers';
import { applyLinePriceFacts } from './linePriceApplication';
import { LineCostNeedsVendorError, lineHasRealVendor } from './lineVendor';
import { lockedUnitRefusal, isLockedAtStatedPrice, LockedLineError } from './statedSelling';
import { catalogueSearchPickMark, costVatAgreesWithLoadedRate, vatRateChangedError } from './cataloguePickDetails';
import { applyPackagingRemark } from '../../utils/packConversion';

export interface AddCatalogueItemFromLineDeps {
  submitCatalogueAddition: (body: SubmitCatalogueAdditionBody) => Promise<SubmitCatalogueAdditionResponse>;
  /** `updateQuotation(quotationId, { items })` — the whole updated quotation back, or a not-`success` envelope. */
  updateQuotationItems: (
    items: QuotationItemDto[]
  ) => Promise<{ success: boolean; data?: QuotationDetailDto }>;
}

export interface AddCatalogueItemFromLineArgs {
  quotationId: string;
  quotationNumber: string | null | undefined;
  lineIndex: number;
  items: QuotationItemDto[];
  payload: CreateCatalogueItemPayload;
  /** US$ per TT$1 — the quotation's own locked rate, or null when it has not loaded. */
  ttdToUsdRate: number | null;
  markupPercentage: number;
  /**
   * The Settings VAT rate (`getVatRate`, `lib/settingsApi.ts`), percent, or
   * null while it has not loaded / has never been set. This form's own VAT
   * box states `payload.vat_applicable` directly — never blank, unlike a
   * catalogue pick — so there is no "Does this item have VAT?" popup on this
   * path, but a `true` box still needs this rate to gross the price
   * (`catalogueRowCostPrice`'s refusal below).
   */
  vatRatePercent: number | null;
}

export async function addCatalogueItemFromLine(
  args: AddCatalogueItemFromLineArgs,
  deps: AddCatalogueItemFromLineDeps
): Promise<QuotationDetailDto> {
  const { quotationId, quotationNumber, lineIndex, items, payload, ttdToUsdRate, markupPercentage, vatRatePercent } =
    args;
  const currentItem = items[lineIndex];
  if (!currentItem) {
    throw new Error('This line no longer exists on the quotation. Reload the page and try again.');
  }

  // D107: no price without a registered vendor. The form requires the vendor, so this only
  // trips on a payload that got here without one - refused BEFORE anything is created, so a
  // refusal leaves no submission behind and the line unpriced.
  if (!lineHasRealVendor({ selectedVendor: payload.supplier })) throw new LineCostNeedsVendorError();
  const vendorName = String(payload.supplier).trim();

  // A locked IFS line is never moved to another unit, and the refusal is made BEFORE the submission
  // is created (`applyLinePriceFacts` refuses too, as the owner, but by then it would be queued).
  // A locked line with no unit stays unit-less (blank = blank is the same unit); any other line defaults to PCS.
  const targetUom =
    String(payload.unit || currentItem.uom || '').trim() || currentItem.uom || (isLockedAtStatedPrice(currentItem) ? '' : 'PCS');
  const lockedRefusal = lockedUnitRefusal(currentItem, targetUom);
  if (lockedRefusal) throw new LockedLineError(lockedRefusal);

  // Grossed for VAT FIRST, through the one owner — `catalogueRowCostPrice`
  // (owner rule, Deborah's VAT report, 2026-09-24: a quotation line's COST is
  // the final VAT-inclusive price USC pays; input VAT is cost). Grossing
  // before converting currency is the same number as grossing after
  // (multiplication commutes), so this stays a single currency conversion.
  // The catalogue SUBMISSION below still carries `payload` unmodified — the
  // typed, VAT-exclusive `item_price` plus its `vat_applicable` flag, exactly
  // as today; only the money that prices THIS line is grossed here.
  //
  // Refused, not guessed (2026-09-24, "one Trinidad VAT rate"): a VAT box
  // ticked with no Settings rate loaded has no honest cost — `costPrice` is
  // null and the currency-conversion refusal below fires on it, the same path
  // a currency with no rate already takes.
  const costPrice = catalogueRowCostPrice(payload, vatRatePercent);

  // Refused, not guessed: a price in a currency this quotation holds no rate
  // for cannot be costed (`fxDisplay.usdCostFacts`, the one owner — invariant
  // 5/8). Checked BEFORE anything is created, so a refusal here creates no
  // submission either.
  // D85.6: a TT$ price is ceiled ONCE from the unrounded conversion, which is kept as
  // the line's exact cost (the markup goes on it).
  const costFacts = usdCostFacts(costPrice, payload.currency, ttdToUsdRate);
  if (costFacts == null) {
    throw new Error(
      payload.vat_applicable && (typeof vatRatePercent !== 'number' || !Number.isFinite(vatRatePercent))
        ? 'VAT rate is not set in Settings, so this item\'s cost cannot be worked out.'
        : `This price is stated in ${payload.currency || 'a currency'}, and this quotation has no exchange rate loaded for it, so it cannot be costed. Reload the page and try again, or enter the price in TT$ or US$.`
    );
  }

  // The submission first. If IT fails — already in the catalogue
  // (CATALOGUE_SUBMISSION_ITEM_EXISTS), already waiting for review
  // (CATALOGUE_SUBMISSION_ALREADY_PENDING), or a bad field
  // (CATALOGUE_SUBMISSION_INVALID) — this throws UNCHANGED (never re-wrapped:
  // the caller switches on `err.code`, never on this file's prose) and the
  // line below is never reached. One failure, one outcome.
  const receipt = await deps.submitCatalogueAddition({
    payload,
    requestedFrom: {
      quotationId,
      quotationNumber: quotationNumber ?? null,
      lineIndex,
      lineItemName: currentItem.itemName ?? null,
    },
  });

  // The receipt's fact is the rate the server read on submission; `costPrice`
  // above is grossed at the rate this page loaded. They must be one reading
  // (`cataloguePickDetails.costVatAgreesWithLoadedRate`): the submission is
  // already queued, so only the line is left unpriced.
  const costVatPercent = receipt?.data?.costVatPercent;
  if (!costVatAgreesWithLoadedRate({ vat_applicable: !!payload.vat_applicable, costVatPercent }, vatRatePercent)) {
    throw vatRateChangedError(
      'The catalogue addition was submitted, but the VAT rate in Settings changed after this page was opened, so this line was not priced. Reload the page and price it again — the submission is still queued for review.'
    );
  }

  const pricedTtd = catalogueRowIsTtd({ currency: payload.currency, source: payload.source });
  const applied = applyLinePriceFacts(currentItem, {
    unitPriceUsd: costFacts.unitPrice,
    exactUnitPriceUsd: costFacts.unitPriceExact,
    unitPriceTtd: pricedTtd && costPrice != null ? ceilPrice(costPrice) : null,
    uom: targetUom,
    vat: !!payload.vat_applicable,
    selectedVendor: vendorName,
    quantity: currentItem.quantity,
    markupPercent: markupToApply(currentItem, markupPercentage),
  });

  // The VAT inside the cost just set (D82): `costPrice` above is the typed
  // price grossed by its VAT box at the Settings rate, and the server states
  // that fact on the receipt (`catalogueRowCostVatPercentOrNull`) — recorded
  // as is, AFTER `applyLinePriceFacts`, which retires the replaced cost's one.
  // A receipt that states none leaves it absent; no VAT maths here.
  const newItem: QuotationItemDto = {
    ...currentItem,
    ...applied.line,
    pricingSource: 'manual_entry',
    pricingDetails: {
      ...applied.pricingDetails,
      // D119.5: a staff pick of a catalogue row, so it carries the pick mark a search pick carries -
      // by it the server withdraws the notes of the match this replaces. Naming the new row.
      catalogueSearchPick: catalogueSearchPickMark({ rowUnit: payload.unit, pricedPer: targetUom, rowName: payload.item_name }),
      ...(costVatPercent === undefined ? {} : { costVatPercent }),
    } as QuotationItemDto['pricingDetails'],
  };
  // The old pack is retired above, so its client-visible packaging sentence
  // goes too (owner decision 2026-09-26).
  Object.assign(newItem, applyPackagingRemark(newItem));
  const updatedItems = [...items];
  updatedItems[lineIndex] = newItem;

  // The submission above is already durably created by this point. If THIS
  // write fails, the line is left unpriced with a genuine submission still
  // queued for review — a deliberate, stated trade-off (two independent
  // writes across two different collections from the client, no shared
  // transaction to wrap them in), not a silent fallback: the error still
  // reaches the caller unchanged.
  const response = await deps.updateQuotationItems(updatedItems);
  if (!response.success || !response.data) {
    throw new Error(
      'The catalogue addition was submitted, but pricing this line failed. Reload and try again — the submission is still queued for review.'
    );
  }

  return response.data;
}
